import { notifyProgress } from '@billy/design-system'
import download from 'downloadjs'
import Papa from 'papaparse'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { NotificationKeys } from '../../../enums/notificationKeys'
import { downloadStatusToTKeys } from '../../../utils/export/downloadStatusToTKeys'
import { sanitizeFileName } from '../../../utils/export/sanitizeFileName'

interface UseCsvExportProps {
  spreadSheetData: (string | number)[][]
  fileName: string
  notificationKey: NotificationKeys
  columns?: string[]
}

export const useCsvExport = ({ spreadSheetData, fileName, notificationKey }: UseCsvExportProps) => {
  const { t } = useTranslation()
  const { organization } = useUserOrganization()

  const organizationName = organization?.name || ''
  const [isLoading, setIsLoading] = useState(false)

  const exportAsCsv = useCallback(async () => {
    try {
      setIsLoading(true)
      const csv = Papa.unparse(spreadSheetData, {
        quotes: true,
        delimiter: ';',
      })

      const blob = new Blob(['\uFEFF', csv], { type: 'text/csv;charset=utf-8;' })
      const finalName = `${fileName}-${sanitizeFileName(organizationName)}.csv`

      download(blob, finalName, 'text/csv')

      const successTKeys = downloadStatusToTKeys.success
      notifyProgress({
        id: notificationKey,
        title: t(successTKeys.title),
        description: t(successTKeys.description),
        variant: 'success',
        progress: 100,
        closable: true,
      })
      setIsLoading(false)
    } catch {
      const errorTKeys = downloadStatusToTKeys.error
      notifyProgress({
        id: notificationKey,
        title: t(errorTKeys.title),
        description: t(errorTKeys.description),
        variant: 'error',
        progress: 100,
        closable: true,
      })
      setIsLoading(false)
    }
  }, [spreadSheetData, fileName, organizationName, notificationKey, t])

  return { exportAsCsv, isLoading }
}
